/**
 * A reusable “Import GPX” modal:
 *  - lets you pick or drag-and-drop a .gpx file
 *  - (optionally) collect a name & description
 *
 * Used for both MyPins (importing pin-only GPX)
 * and CaptainsLog (importing named/description trips).
 */

import { DialogContent, DialogActions, Button, Typography, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useState } from "react";

import GpxDropzone from "@/components/GpxDropzone";

interface IImportGpxModal {
  header: string;
  file: File | null;
  loading: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => Promise<any> | void;
  onUploadFile: (file: File | null) => void;
  showNameDesc: boolean;
}

function ImportGpxModal({
  header,
  file,
  loading,
  onClose,
  onUploadFile,
  onSave,
  showNameDesc,
}: IImportGpxModal) {
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  return (
    <>
      <DialogContent sx={{ pt: 0, pb: 0 }}>
        <Typography color="text.secondary" sx={{ mb: "12px" }} variant="body1">
          {header}
        </Typography>
        <GpxDropzone action={onUploadFile} file={file} />
        {showNameDesc && (
          <>
            <TextField
              fullWidth
              label="Name"
              placeholder="e.g. St. Michaels, Maryland"
              size="small"
              sx={{ mt: 2 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              label="Description"
              placeholder="e.g. An awesome trip on the Chesapeake Bay with friends"
              rows={3}
              size="small"
              sx={{ mt: 2 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button type="button" onClick={onClose}>
          Discard
        </Button>
        <LoadingButton
          autoFocus
          loading={loading}
          variant="contained"
          onClick={() => onSave(name, description)}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </>
  );
}

export default ImportGpxModal;
